import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Edit, Trash2, Save, X, Image as ImageIcon, PawPrint, DollarSign, Home } from 'lucide-react';
import { getProperties, createProperty, updateProperty, deleteProperty, getCompanyInfo, updateCompanyInfo, getQualifications, updateQualifications, getFAQs } from '../services/api';
import { toast } from 'sonner';
import { MediaLibrary } from '../components/MediaLibrary';
import { FAQManager } from '../components/FAQManager';
import { RichTextEditor } from '../components/RichTextEditor';
import { SiteSettingsManager } from '../components/SiteSettingsManager';
import { AboutPageManager } from '../components/AboutPageManager';
import { ChangeCredentials } from '../components/ChangeCredentials';
import { AppointmentsManager } from '../components/AppointmentsManager';
import { HomeContentManager } from '../components/HomeContentManager';
import { TeamMemberManager } from '../components/TeamMemberManager';
import { ContactPageManager } from '../components/ContactPageManager';

const Admin = () => {
  const [properties, setProperties] = useState([]);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [qualifications, setQualifications] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProperty, setEditingProperty] = useState(null);
  const [showPropertyForm, setShowPropertyForm] = useState(false);
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [mediaSelectionMode, setMediaSelectionMode] = useState(null); // 'main' or 'gallery'

  // Form states
  const [propertyForm, setPropertyForm] = useState({
    title: '',
    address: '',
    price: '',
    bedrooms: '',
    bathrooms: '',
    sqft: '',
    image: '',
    images: [],
    status: 'available',
    description: '',
    applicationLink: '',
    application_requirements: '',
    pets_allowed: 'no',
    pet_policy: '',
    pet_deposit: 0,
    pet_rent: 0
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [propsData, companyData, qualsData, faqsData] = await Promise.all([
        getProperties(),
        getCompanyInfo(),
        getQualifications(),
        getFAQs()
      ]);
      setProperties(propsData);
      setCompanyInfo(companyData);
      setQualifications(qualsData);
      setFaqs(faqsData);
    } catch (error) {
      toast.error('Failed to load data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePropertySubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...propertyForm,
        price: parseFloat(propertyForm.price),
        bedrooms: parseInt(propertyForm.bedrooms),
        bathrooms: parseFloat(propertyForm.bathrooms),
        sqft: parseInt(propertyForm.sqft),
        pet_deposit: parseFloat(propertyForm.pet_deposit) || 0,
        pet_rent: parseFloat(propertyForm.pet_rent) || 0
      };

      if (editingProperty) {
        await updateProperty(editingProperty.id, data);
        toast.success('Property updated successfully');
      } else {
        await createProperty(data);
        toast.success('Property created successfully');
      }

      setShowPropertyForm(false);
      setEditingProperty(null);
      setPropertyForm({
        title: '',
        address: '',
        price: '',
        bedrooms: '',
        bathrooms: '',
        sqft: '',
        image: '',
        images: [],
        status: 'available',
        description: '',
        applicationLink: '',
        application_requirements: '',
        pets_allowed: 'no',
        pet_policy: '',
        pet_deposit: 0,
        pet_rent: 0
      });
      loadData();
    } catch (error) {
      toast.error('Failed to save property');
      console.error(error);
    }
  };

  const handleEditProperty = (property) => {
    setEditingProperty(property);
    setPropertyForm({
      title: property.title,
      address: property.address,
      price: property.price.toString(),
      bedrooms: property.bedrooms.toString(),
      bathrooms: property.bathrooms.toString(),
      sqft: property.sqft.toString(),
      image: property.image,
      images: property.images || [],
      status: property.status,
      description: property.description,
      applicationLink: property.applicationLink || '',
      application_requirements: property.application_requirements || '',
      pets_allowed: property.pets_allowed || 'no',
      pet_policy: property.pet_policy || '',
      pet_deposit: property.pet_deposit || 0,
      pet_rent: property.pet_rent || 0
    });
    setShowPropertyForm(true);
  };

  const handleMediaSelect = (selectedUrls) => {
    if (mediaSelectionMode === 'main') {
      setPropertyForm({ ...propertyForm, image: selectedUrls[0] });
    } else if (mediaSelectionMode === 'gallery') {
      setPropertyForm({ ...propertyForm, images: [...propertyForm.images, ...selectedUrls] });
    }
  };

  const removeGalleryImage = (index) => {
    const newImages = propertyForm.images.filter((_, i) => i !== index);
    setPropertyForm({ ...propertyForm, images: newImages });
  };

  const handleDeleteProperty = async (id) => {
    if (!window.confirm('Are you sure you want to delete this property?')) return;
    
    try {
      await deleteProperty(id);
      toast.success('Property deleted successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to delete property');
      console.error(error);
    }
  };

  const handleUpdateCompanyInfo = async (e) => {
    e.preventDefault();
    try {
      await updateCompanyInfo(companyInfo);
      toast.success('Company information updated successfully');
    } catch (error) {
      toast.error('Failed to update company information');
      console.error(error);
    }
  };

  const handleUpdateQualifications = async (e) => {
    e.preventDefault();
    try {
      await updateQualifications(qualifications);
      toast.success('Qualifications updated successfully');
    } catch (error) {
      toast.error('Failed to update qualifications');
      console.error(error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <p className="text-xl text-gray-600 dark:text-gray-400">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-24 pb-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">Admin Dashboard</h1>
          <p className="text-gray-600 dark:text-gray-400">Manage properties and website content</p>
        </div>

        <Tabs defaultValue="properties" className="w-full">
          <TabsList className="grid w-full max-w-6xl grid-cols-10 gap-1 text-xs">
            <TabsTrigger value="properties">Properties</TabsTrigger>
            <TabsTrigger value="home">Home Page</TabsTrigger>
            <TabsTrigger value="team">Team</TabsTrigger>
            <TabsTrigger value="contact">Contact</TabsTrigger>
            <TabsTrigger value="appointments">Appointments</TabsTrigger>
            <TabsTrigger value="company">Company</TabsTrigger>
            <TabsTrigger value="qualifications">Qualifications</TabsTrigger>
            <TabsTrigger value="faqs">FAQs</TabsTrigger>
            <TabsTrigger value="about">About</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
            <TabsTrigger value="credentials">Security</TabsTrigger>
          </TabsList>

          {/* Properties Tab */}
          <TabsContent value="properties" className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Property Management</h2>
                <Button
                onClick={() => {
                  setShowPropertyForm(true);
                  setEditingProperty(null);
                  setPropertyForm({
                    title: '',
                    address: '',
                    price: '',
                    bedrooms: '',
                    bathrooms: '',
                    sqft: '',
                    image: '',
                    images: [],
                    status: 'available',
                    description: '',
                    applicationLink: '',
                    application_requirements: '',
                    pets_allowed: 'no',
                    pet_policy: '',
                    pet_deposit: 0,
                    pet_rent: 0
                  });
                }}
                className="bg-otter-green hover:bg-otter-green-600 text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Property
              </Button>
            </div>

            {showPropertyForm && (
              <Card className="dark:bg-gray-900 dark:border-gray-800">
                <CardHeader>
                  <CardTitle className="dark:text-white">
                    {editingProperty ? 'Edit Property' : 'Add New Property'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handlePropertySubmit} className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="title">Title</Label>
                        <Input
                          id="title"
                          value={propertyForm.title}
                          onChange={(e) => setPropertyForm({ ...propertyForm, title: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="address">Address</Label>
                        <Input
                          id="address"
                          value={propertyForm.address}
                          onChange={(e) => setPropertyForm({ ...propertyForm, address: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="price">Monthly Rent ($)</Label>
                        <Input
                          id="price"
                          type="number"
                          value={propertyForm.price}
                          onChange={(e) => setPropertyForm({ ...propertyForm, price: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="status">Status</Label>
                        <Select value={propertyForm.status} onValueChange={(value) => setPropertyForm({ ...propertyForm, status: value })}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="available">Available</SelectItem>
                            <SelectItem value="rented">Rented</SelectItem>
                            <SelectItem value="coming_soon">Coming Soon</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="bedrooms">Bedrooms</Label>
                        <Input
                          id="bedrooms"
                          type="number"
                          value={propertyForm.bedrooms}
                          onChange={(e) => setPropertyForm({ ...propertyForm, bedrooms: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="bathrooms">Bathrooms</Label>
                        <Input
                          id="bathrooms"
                          type="number"
                          step="0.5"
                          value={propertyForm.bathrooms}
                          onChange={(e) => setPropertyForm({ ...propertyForm, bathrooms: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="sqft">Square Feet</Label>
                        <Input
                          id="sqft"
                          type="number"
                          value={propertyForm.sqft}
                          onChange={(e) => setPropertyForm({ ...propertyForm, sqft: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                    
                    {/* Main Image */}
                    <div>
                      <Label>Main Image</Label>
                      <div className="flex gap-2">
                        <Input
                          value={propertyForm.image}
                          onChange={(e) => setPropertyForm({ ...propertyForm, image: e.target.value })}
                          placeholder="Image URL"
                          required
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setMediaSelectionMode('main');
                            setShowMediaLibrary(true);
                          }}
                        >
                          <ImageIcon className="h-4 w-4 mr-2" />
                          Choose
                        </Button>
                      </div>
                      {propertyForm.image && (
                        <img 
                          src={propertyForm.image.startsWith('/uploads') 
                            ? `${process.env.REACT_APP_BACKEND_URL}${propertyForm.image}` 
                            : propertyForm.image} 
                          alt="Preview" 
                          className="mt-2 h-32 w-32 object-cover rounded"
                        />
                      )}
                    </div>

                    {/* Image Gallery */}
                    <div>
                      <Label>Additional Images (Gallery)</Label>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setMediaSelectionMode('gallery');
                          setShowMediaLibrary(true);
                        }}
                        className="w-full"
                      >
                        <ImageIcon className="h-4 w-4 mr-2" />
                        Add Images to Gallery
                      </Button>
                      {propertyForm.images.length > 0 && (
                        <div className="mt-2 grid grid-cols-6 gap-2">
                          {propertyForm.images.map((img, index) => (
                            <div key={index} className="relative group">
                              <img 
                                src={img.startsWith('/uploads') 
                                  ? `${process.env.REACT_APP_BACKEND_URL}${img}` 
                                  : img}
                                alt={`Gallery ${index + 1}`}
                                className="h-20 w-20 object-cover rounded"
                              />
                              <button
                                type="button"
                                onClick={() => removeGalleryImage(index)}
                                className="absolute top-0 right-0 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <Label htmlFor="description">Description</Label>
                      <RichTextEditor
                        value={propertyForm.description}
                        onChange={(value) => setPropertyForm({ ...propertyForm, description: value })}
                        placeholder="Enter property description..."
                      />
                    </div>
                    <div>
                      <Label htmlFor="applicationLink">Application Link (Optional)</Label>
                      <Input
                        id="applicationLink"
                        type="url"
                        value={propertyForm.applicationLink}
                        onChange={(e) => setPropertyForm({ ...propertyForm, applicationLink: e.target.value })}
                        placeholder="https://example.com/apply"
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        Add a link where tenants can apply for this property
                      </p>
                    </div>

                    <div className="col-span-2">
                      <Label htmlFor="application_requirements">Application Requirements (Optional)</Label>
                      <RichTextEditor
                        value={propertyForm.application_requirements}
                        onChange={(value) => setPropertyForm({ ...propertyForm, application_requirements: value })}
                        placeholder="Enter application requirements (e.g., Income 3x rent, Credit score 600+)..."
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        Specify requirements for tenants applying to this property. If left blank, default requirements will be shown.
                      </p>
                    </div>

                    {/* Pet Policy Section */}
                    <div className="col-span-2 border-t pt-6">
                      <h3 className="text-lg font-semibold mb-4 dark:text-white flex items-center gap-2">
                        <PawPrint className="h-5 w-5 text-otter-green" />
                        Pet Policy
                      </h3>
                      
                      <div className="grid md:grid-cols-3 gap-6 mb-4">
                        {/* Pets Allowed */}
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-otter-green/10 rounded-lg">
                            <PawPrint className="h-5 w-5 text-otter-green" />
                          </div>
                          <div className="flex-1">
                            <Label htmlFor="pets_allowed" className="text-sm font-medium mb-1">Pets Allowed</Label>
                            <select
                              id="pets_allowed"
                              value={propertyForm.pets_allowed || 'no'}
                              onChange={(e) => setPropertyForm({ ...propertyForm, pets_allowed: e.target.value })}
                              className="w-full p-2 border rounded dark:bg-gray-800 dark:text-white text-sm"
                            >
                              <option value="no">No Pets</option>
                              <option value="yes">Yes - All Pets</option>
                              <option value="cats_only">Cats Only</option>
                              <option value="dogs_only">Dogs Only</option>
                              <option value="case_by_case">Case by Case</option>
                            </select>
                          </div>
                        </div>

                        {/* Pet Deposit */}
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-otter-green/10 rounded-lg">
                            <DollarSign className="h-5 w-5 text-otter-green" />
                          </div>
                          <div className="flex-1">
                            <Label htmlFor="pet_deposit" className="text-sm font-medium mb-1">Pet Deposit</Label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                              <Input
                                id="pet_deposit"
                                type="number"
                                value={propertyForm.pet_deposit || 0}
                                onChange={(e) => setPropertyForm({ ...propertyForm, pet_deposit: parseFloat(e.target.value) || 0 })}
                                placeholder="0"
                                min="0"
                                className="pl-7"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Monthly Pet Rent */}
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-otter-green/10 rounded-lg">
                            <Home className="h-5 w-5 text-otter-green" />
                          </div>
                          <div className="flex-1">
                            <Label htmlFor="pet_rent" className="text-sm font-medium mb-1">Monthly Pet Rent</Label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                              <Input
                                id="pet_rent"
                                type="number"
                                value={propertyForm.pet_rent || 0}
                                onChange={(e) => setPropertyForm({ ...propertyForm, pet_rent: parseFloat(e.target.value) || 0 })}
                                placeholder="0"
                                min="0"
                                className="pl-7"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Pet Policy Details */}
                      <div>
                        <Label htmlFor="pet_policy" className="text-sm font-medium mb-1">Pet Policy Details</Label>
                        <Textarea
                          id="pet_policy"
                          value={propertyForm.pet_policy || ''}
                          onChange={(e) => setPropertyForm({ ...propertyForm, pet_policy: e.target.value })}
                          rows={3}
                          placeholder="E.g., Maximum 2 pets, weight restrictions, breed restrictions..."
                          className="resize-none"
                        />
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button type="submit" className="bg-otter-green hover:bg-otter-green-600 text-white">
                        <Save className="h-4 w-4 mr-2" />
                        Save Property
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setShowPropertyForm(false);
                          setEditingProperty(null);
                        }}
                      >
                        <X className="h-4 w-4 mr-2" />
                        Cancel
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {properties.map((property) => (
                <Card key={property.id} className="dark:bg-gray-900 dark:border-gray-800">
                  <div className="relative h-48">
                    <img
                      src={property.image}
                      alt={property.title}
                      className="w-full h-full object-cover rounded-t-lg"
                    />
                    <span className={`absolute top-2 right-2 px-2 py-1 rounded text-xs font-semibold ${
                      property.status === 'available' ? 'bg-otter-green' : 
                      property.status === 'coming_soon' ? 'bg-otter-teal' : 'bg-gray-500'
                    } text-white`}>
                      {property.status === 'coming_soon' ? 'Coming Soon' : property.status}
                    </span>
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-bold text-lg mb-1 dark:text-white">{property.title}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{property.address}</p>
                    <p className="text-xl font-bold text-otter-green dark:text-otter-green-400 mb-3">${property.price}/mo</p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditProperty(property)}
                        className="flex-1"
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteProperty(property.id)}
                        className="flex-1 text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Company Info Tab */}
          <TabsContent value="company">
            <Card className="dark:bg-gray-900 dark:border-gray-800">
              <CardHeader>
                <CardTitle className="dark:text-white">Company Information</CardTitle>
              </CardHeader>
              <CardContent>
                {companyInfo && (
                  <form onSubmit={handleUpdateCompanyInfo} className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name">Company Name</Label>
                        <Input
                          id="name"
                          value={companyInfo.name}
                          onChange={(e) => setCompanyInfo({ ...companyInfo, name: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="location">Location</Label>
                        <Input
                          id="location"
                          value={companyInfo.location}
                          onChange={(e) => setCompanyInfo({ ...companyInfo, location: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">Phone</Label>
                        <Input
                          id="phone"
                          value={companyInfo.phone}
                          onChange={(e) => setCompanyInfo({ ...companyInfo, phone: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="email">Email</Label>
                        <Input
                          id="email"
                          type="email"
                          value={companyInfo.email}
                          onChange={(e) => setCompanyInfo({ ...companyInfo, email: e.target.value })}
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="address">Address</Label>
                      <Input
                        id="address"
                        value={companyInfo.address}
                        onChange={(e) => setCompanyInfo({ ...companyInfo, address: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="about">About Us</Label>
                      <RichTextEditor
                        value={companyInfo.about}
                        onChange={(value) => setCompanyInfo({ ...companyInfo, about: value })}
                        placeholder="Enter company about text"
                      />
                    </div>
                    <div>
                      <Label htmlFor="mission">Mission Statement</Label>
                      <RichTextEditor
                        value={companyInfo.mission}
                        onChange={(value) => setCompanyInfo({ ...companyInfo, mission: value })}
                        placeholder="Enter mission statement"
                      />
                    </div>
                    <Button type="submit" className="bg-otter-green hover:bg-otter-green-600 text-white">
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Qualifications Tab */}
          <TabsContent value="qualifications">
            <Card className="dark:bg-gray-900 dark:border-gray-800">
              <CardHeader>
                <CardTitle className="dark:text-white">Tenant Qualifications</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleUpdateQualifications} className="space-y-4">
                  {qualifications.map((qual, index) => (
                    <div key={qual.id} className="p-4 border rounded-lg dark:border-gray-700">
                      <div className="grid gap-4">
                        <div>
                          <Label>Title</Label>
                          <Input
                            value={qual.title}
                            onChange={(e) => {
                              const updated = [...qualifications];
                              updated[index].title = e.target.value;
                              setQualifications(updated);
                            }}
                          />
                        </div>
                        <div>
                          <Label>Description</Label>
                          <RichTextEditor
                            value={qual.description}
                            onChange={(value) => {
                              const updated = [...qualifications];
                              updated[index].description = value;
                              setQualifications(updated);
                            }}
                            placeholder="Enter qualification description"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  <Button type="submit" className="bg-otter-green hover:bg-otter-green-600 text-white">
                    <Save className="h-4 w-4 mr-2" />
                    Save Qualifications
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* FAQs Tab */}
          <TabsContent value="faqs">
            <Card className="dark:bg-gray-900 dark:border-gray-800">
              <CardHeader>
                <CardTitle className="dark:text-white">Frequently Asked Questions</CardTitle>
              </CardHeader>
              <CardContent>
                <FAQManager faqs={faqs} onFaqsChange={setFaqs} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Home Page Tab */}
          <TabsContent value="home">
            <HomeContentManager />
          </TabsContent>

          {/* Team Members Tab */}
          <TabsContent value="team">
            <TeamMemberManager />
          </TabsContent>

          {/* Contact Page Tab */}
          <TabsContent value="contact">
            <ContactPageManager />
          </TabsContent>

          {/* Appointments Tab */}
          <TabsContent value="appointments">
            <AppointmentsManager />
          </TabsContent>

          {/* About Page Tab */}
          <TabsContent value="about">
            <AboutPageManager />
          </TabsContent>

          {/* Site Settings Tab */}
          <TabsContent value="settings">
            <SiteSettingsManager />
          </TabsContent>

          {/* Security/Credentials Tab */}
          <TabsContent value="credentials">
            <ChangeCredentials />
          </TabsContent>
        </Tabs>
      </div>

      {/* Media Library Modal */}
      <MediaLibrary
        isOpen={showMediaLibrary}
        onClose={() => {
          setShowMediaLibrary(false);
          setMediaSelectionMode(null);
        }}
        onSelect={handleMediaSelect}
      />
    </div>
  );
};

export default Admin;
